import { HttpClient } from '@angular/common/http';
import { Injectable, computed, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, tap } from 'rxjs';
import { environment } from '../../environments/environment';

interface TokenPair { access: string; refresh: string; }
interface MeResponse { id: number; username: string; display_name: string; }

const ACCESS_KEY = 'mais_access';
const REFRESH_KEY = 'mais_refresh';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private http = inject(HttpClient);
  private router = inject(Router);

  private accessToken = signal<string | null>(localStorage.getItem(ACCESS_KEY));
  readonly isLoggedIn = computed(() => !!this.accessToken());
  readonly currentUser = signal<MeResponse | null>(null);

  getAccessToken(): string | null { return this.accessToken(); }
  getRefreshToken(): string | null { return localStorage.getItem(REFRESH_KEY); }

  login(username: string, password: string): Observable<TokenPair> {
    return this.http
      .post<TokenPair>(`${environment.apiBaseUrl}/auth/login/`, { username, password })
      .pipe(tap(t => this.setTokens(t.access, t.refresh)));
  }

  refresh(): Observable<{ access: string }> {
    return this.http
      .post<{ access: string }>(`${environment.apiBaseUrl}/auth/refresh/`, {
        refresh: this.getRefreshToken(),
      })
      .pipe(tap(r => this.setAccess(r.access)));
  }

  loadMe(): Observable<MeResponse> {
    return this.http
      .get<MeResponse>(`${environment.apiBaseUrl}/auth/me/`)
      .pipe(tap(u => this.currentUser.set(u)));
  }

  logout(): void {
    localStorage.removeItem(ACCESS_KEY);
    localStorage.removeItem(REFRESH_KEY);
    this.accessToken.set(null);
    this.currentUser.set(null);
    this.router.navigateByUrl('/login');
  }

  private setTokens(access: string, refresh: string): void {
    localStorage.setItem(ACCESS_KEY, access);
    localStorage.setItem(REFRESH_KEY, refresh);
    this.accessToken.set(access);
  }

  private setAccess(access: string): void {
    localStorage.setItem(ACCESS_KEY, access);
    this.accessToken.set(access);
  }
}
